<script setup lang="ts">
/**
 * A plan view of the battle: allies green, enemies red, you in the middle.
 *
 * ── Why a canvas and not divs ────────────────────────────────────────────────
 * Every other HUD overlay is DOM, because a nameplate is a health bar that
 * happens to follow a tank and DOM is the cheaper way to draw text. A radar is
 * the opposite: a dozen filled circles, two rings, a clipped boundary and a
 * wedge, all redrawn every frame. As DOM that is a dozen absolutely-positioned
 * nodes whose styles are rewritten thirty times a second, and the browser has to
 * re-layout each time. One canvas draw is a handful of path operations on a
 * 168 px square.
 *
 * ── Why it is painted rather than photographed ───────────────────────────────
 * A generated bezel image was considered and rejected. This is the same argument
 * `environment.ts` makes for the sky: what the frame needs is a crisp ring, a few
 * tick marks and a grid, and those are exactly what a raster loses. The scope is
 * ~170 px on a 1x display and ~340 device pixels on a 2x one, so any fixed image
 * is soft on half the machines that run it; the canvas is redrawn at
 * `devicePixelRatio` and is sharp on all of them. It also inherits the HUD's own
 * palette by construction rather than by somebody matching hex values to a
 * picture. Art would earn its place here if the bezel wanted *texture* —
 * scratched metal, a glass reflection — which is a different design than the
 * flat translucent panels the rest of this HUD is built from.
 *
 * ── What it deliberately does not do ─────────────────────────────────────────
 * It does not rotate with the tank. North stays up, so a position on the scope
 * means the same thing from one second to the next and the arena boundary can be
 * read as a landmark; a scope that spun with the hull would turn every glance
 * into a re-orientation. Which way you face is carried by the arrow and the
 * wedge instead, which is the part that actually changes.
 *
 * It is also **read-only, and has no pointer handlers at all**. Aiming holds
 * pointer lock, so the cursor cannot reach a HUD control exactly when the player
 * would want one — the same reason the orders panel lists labels rather than
 * buttons.
 */
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import type { RadarState } from '@/game/arena-scene';
import { clampToScope, scopeAngleFor, scopeOffset, scopeOffsetTo } from './radar-geometry';

const props = defineProps<{
  radar?: RadarState;
  /**
   * Smaller, and moved to the top right.
   *
   * A touch build puts a thumb in the bottom-right corner, and measured at every
   * viewport from an iPhone SE to an iPad the fire circle lands almost entirely
   * on this scope — 5,776 px² of overlap at 667×375 and 12,544 px² at 1180×820.
   * A radar under a thumb is a radar nobody can read, so it moves rather than
   * shrinking in place.
   *
   * Only the CSS changes: `resize()` reads `el.clientWidth`, so the canvas
   * follows the box and stays sharp at `devicePixelRatio` — which is the whole
   * reason this is drawn rather than fetched, and why shrinking it costs nothing.
   */
  compact?: boolean;
}>();

/**
 * Contact counts, published onto the element as data attributes.
 *
 * The scope is a canvas, and a canvas is opaque to every check the visual
 * harness prefers — counting coloured pixels would be measuring anti-aliasing
 * and would confuse two overlapping blips with one. These say what the radar
 * *believes*, so a case can assert it against the arena's own roster rather than
 * against a photograph. See `frontend/e2e/README.md` on preferring a probe to a
 * pixel.
 */
const allyCount = computed(() => (props.radar?.contacts ?? []).filter((c) => c.friendly).length);
const enemyCount = computed(() => (props.radar?.contacts ?? []).filter((c) => !c.friendly).length);

const canvas = ref<HTMLCanvasElement | null>(null);

/**
 * Metres from the player to the rim of the scope.
 *
 * 100 m against an arena that is 200 m across, so from the middle of the map the
 * scope reaches every wall and from a corner it still covers the half of the
 * battlefield anyone can shoot you from. It is a constant rather than the map's
 * own `arenaHalfExtent` on purpose: the scope's *scale* should not change
 * between maps, or a player would have to re-learn what "close" looks like every
 * time they picked a different battlefield.
 *
 * **This is the knob that decides how much a radar gives away.** At 100 m it
 * shows essentially the whole enemy team, which is a real change to how much can
 * be hidden — drop it and concealment comes back.
 */
const RANGE_M = 100;

/** Radius of a contact blip, and of the ring drawn for one clamped to the rim. */
const BLIP_R = 3.6;

const COLOURS = {
  ally: '#4FE06A',
  enemy: '#FF4A38',
  // The local arrow takes the accent orange the garage and the muzzle flash use,
  // so "that one is me" needs no legend.
  self: '#E8722C',
  grid: 'rgba(190, 214, 224, 0.16)',
  rim: 'rgba(190, 214, 224, 0.38)',
  bounds: 'rgba(190, 214, 224, 0.30)',
  wedge: 'rgba(232, 114, 44, 0.13)',
  // The mission's own furniture. The road and the uplink are quiet — they are
  // the map, and they never change — while the two things that move take the
  // colours the HUD already uses for "yours" and "look at this".
  route: 'rgba(190, 214, 224, 0.34)',
  objective: '#E8CB4C',
  gate: 'rgba(255, 74, 56, 0.55)',
};

/**
 * The half-width of a mission marker's diamond.
 *
 * A little larger than a contact blip, because these are the two or three things
 * on the scope that are not tanks and have to be distinguishable from one at a
 * glance rather than by colour alone — the scope is read in the second between
 * shots.
 */
const MARKER_R = 5;

/** A diamond centred on the scope point, filled. Used for the movers. */
function diamond(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r, y);
  ctx.closePath();
}

let frame = 0;

function draw(): void {
  const el = canvas.value;
  const radar = props.radar;
  if (!el) return;

  const ctx = el.getContext('2d');
  if (!ctx) return;

  // Re-read the ratio every draw rather than caching it: a window dragged
  // between a laptop screen and an external monitor changes it, and a cached one
  // leaves the scope soft or oversized for the rest of the match.
  const ratio = Math.min(window.devicePixelRatio || 1, 3);
  const size = el.clientWidth || 168;
  const px = Math.round(size * ratio);
  if (el.width !== px || el.height !== px) {
    el.width = px;
    el.height = px;
  }

  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.clearRect(0, 0, size, size);

  const c = size / 2;
  const r = c - 2;
  // Metres to scope pixels. Everything below is drawn in metres and scaled here,
  // so the geometry reads as the map rather than as pixel arithmetic.
  const k = r / RANGE_M;

  ctx.save();
  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.clip();

  // Backdrop, grid and the wedge all live inside the clip, so nothing spills
  // past the rim and the scope needs no second mask.
  // Dark enough that a green blip on grass and a red one on a road read the
  // same. At 0.62 the battlefield came through strongly enough to tint the
  // whole scope and the boundary line disappeared into it.
  ctx.fillStyle = 'rgba(8, 12, 16, 0.82)';
  ctx.fillRect(0, 0, size, size);

  if (radar) {
    // The arena wall, translated by where the player is standing. This is what
    // makes a north-up scope tell you *where* you are as well as who is near:
    // drive into a corner and two edges close in on you.
    // Per axis. The boundary is a *rectangle* and one figure would draw a square
    // — wrong shape, thirty times a second, on the readout whose whole job is
    // telling the player which walls are closing in on them.
    //
    // **Through `scopeOffset`, like the contacts and the wedge**, and that is a
    // repair rather than tidiness. This was the one thing on the scope drawing its
    // own mapping — `c + (-hz - z) * k`, which is world z *unnegated* — so the
    // boundary was mirrored about the centre in z while every blip beside it was
    // not. It is invisible at z = 0 and grows with distance from the centre line,
    // which is why it survived the mirror fix in `5da209a` and the per-axis change
    // after it: on a 200 m square the worst case was a wall drawn 6 m the wrong
    // side of you. On the first 420 m map it is unmistakable — driven onto the
    // north wall, the scope drew that wall *behind* the tank. Measured off the
    // canvas at four walls before the fix: x correct to a pixel on all four, z
    // reflected on all four.
    const hx = radar.arenaHalfExtent.x;
    const hz = radar.arenaHalfExtent.z;
    // The corner nearest the top-left of the scope: world (-hx, +hz), because
    // screen +y is world -z.
    const corner = scopeOffset(-hx - radar.x, hz - radar.z, k);
    ctx.strokeStyle = COLOURS.bounds;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(c + corner.x, c + corner.y, 2 * hx * k, 2 * hz * k);

    // Where the camera is looking, through the tested mapping rather than by
    // hand — this is the line that shipped mirrored.
    const half = 0.42;
    const bearing = scopeAngleFor(radar.turretYaw);
    ctx.beginPath();
    ctx.moveTo(c, c);
    ctx.arc(c, c, r, bearing - half, bearing + half);
    ctx.closePath();
    ctx.fillStyle = COLOURS.wedge;
    ctx.fill();
  }

  ctx.strokeStyle = COLOURS.grid;
  ctx.lineWidth = 1;
  for (const ring of [r / 3, (r * 2) / 3]) {
    ctx.beginPath();
    ctx.arc(c, c, ring, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.moveTo(c, c - r);
  ctx.lineTo(c, c + r);
  ctx.moveTo(c - r, c);
  ctx.lineTo(c + r, c);
  ctx.stroke();

  if (radar) {
    // ── The mission's furniture ─────────────────────────────────────────────
    //
    // Under the contacts, because a tank is always the more urgent thing on this
    // instrument — the road and the gates are where the fight *will* be, and a
    // blip is where it is.
    //
    // **Every point composes through `scopeOffsetTo`.** That is not tidiness: the
    // boundary rectangle computed its own mapping for months and was mirrored in
    // z the whole time, which is exactly the failure a route drawn by hand would
    // reproduce — a convoy road running the wrong way up the valley is entirely
    // plausible on screen.
    const mission = radar.mission;
    if (mission) {
      const at = (p: { x: number; z: number }) => scopeOffsetTo(p, radar, k);

      if (mission.route && mission.route.length > 1) {
        // Drawn unclamped and left to the clip: a road is a *shape*, and pulling
        // its far end onto the rim would bend it into a route nobody drives.
        ctx.beginPath();
        mission.route.forEach((point, i) => {
          const o = at(point);
          if (i === 0) ctx.moveTo(c + o.x, c + o.y);
          else ctx.lineTo(c + o.x, c + o.y);
        });
        ctx.strokeStyle = COLOURS.route;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 4]);
        ctx.stroke();
        ctx.setLineDash([]);

        // The terminus, which is the only waypoint worth marking: it is what
        // winning looks like, and the rest are only where the convoy turns.
        const end = clampToScope(at(mission.route[mission.route.length - 1]), r - MARKER_R - 1);
        ctx.beginPath();
        ctx.arc(c + end.x, c + end.y, MARKER_R - 1, 0, Math.PI * 2);
        ctx.strokeStyle = COLOURS.objective;
        ctx.lineWidth = 1.6;
        ctx.stroke();
      }

      if (mission.gates) {
        // Lit the moment a wave comes out of them and fading over the next
        // couple of seconds. The strength is a subtraction of ticks — see
        // `gateFlash` — so this is not eased on frame time.
        const flash = mission.gateFlash ?? 0;
        for (const gate of mission.gates) {
          const o = clampToScope(at(gate), r - MARKER_R - 1);
          ctx.beginPath();
          ctx.arc(c + o.x, c + o.y, MARKER_R + flash * 4, 0, Math.PI * 2);
          ctx.strokeStyle = COLOURS.gate;
          ctx.lineWidth = 1 + flash * 2;
          ctx.stroke();
        }
      }

      // The tutorial's objective — the `supply` drill's crate — drawn at the size
      // the pickup is actually measured against: `scopeOffsetTo` for the centre
      // and the same `k` for the radius, so a ring on a scope at any range still
      // describes the same metres. Composed through `at` like everything else
      // here: the boundary rectangle spent months computing its own mapping and
      // was mirrored in z the whole time.
      //
      // It used to be the marked ground of the `hold` drill, which is gone with
      // commanding; the field was renamed rather than a second ring added, so
      // this mark has one meaning at a time.
      if (mission.supply) {
        const o = clampToScope(at(mission.supply), r - MARKER_R - 1);
        ctx.beginPath();
        ctx.arc(c + o.x, c + o.y, Math.max(MARKER_R, mission.supply.radius * k), 0, Math.PI * 2);
        ctx.strokeStyle = COLOURS.ally;
        ctx.lineWidth = 1.4;
        ctx.setLineDash([4, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (mission.captureRing) {
        const o = clampToScope(at(mission.captureRing), r - MARKER_R - 1);
        ctx.beginPath();
        ctx.arc(c + o.x, c + o.y, MARKER_R + 2, 0, Math.PI * 2);
        ctx.strokeStyle = COLOURS.ally;
        ctx.lineWidth = 1.4;
        ctx.setLineDash([3, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    for (const contact of radar.contacts) {
      // One mapping for contacts and for the wedge, so the two can never
      // disagree about which way is which.
      const { x: dx, y: dy } = scopeOffset(contact.x - radar.x, contact.z - radar.z, k);
      let sx = c + dx;
      let sy = c + dy;

      const dist = Math.hypot(dx, dy);
      const limit = r - BLIP_R - 1;
      const beyond = dist > limit;
      if (beyond && dist > 0) {
        // Clamped to the rim rather than dropped. A contact you cannot see is
        // the one worth knowing about, and pinning it to the edge keeps its
        // *bearing* honest while its distance stops meaning anything — which is
        // why the rim marker is hollow and the near one is filled.
        sx = c + (dx / dist) * limit;
        sy = c + (dy / dist) * limit;
      }

      const colour = contact.friendly ? COLOURS.ally : COLOURS.enemy;
      ctx.beginPath();
      ctx.arc(sx, sy, BLIP_R, 0, Math.PI * 2);
      if (beyond) {
        ctx.strokeStyle = colour;
        ctx.lineWidth = 1.6;
        ctx.stroke();
      } else {
        ctx.fillStyle = colour;
        ctx.fill();
        // A dark rim under the fill, so two contacts overlapping still read as
        // two and a blip never dissolves into the backdrop behind it.
        ctx.strokeStyle = 'rgba(6, 10, 14, 0.85)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // The two things that move and are not tanks, over the blips rather than
    // under them: the convoy diamond sits on the convoy's own contact and would
    // otherwise be hidden by it, and the core is the object the whole mission is
    // about.
    if (radar.mission?.convoy) {
      const o = clampToScope(scopeOffsetTo(radar.mission.convoy, radar, k), r - MARKER_R - 1);
      diamond(ctx, c + o.x, c + o.y, MARKER_R);
      ctx.fillStyle = COLOURS.objective;
      ctx.fill();
      ctx.strokeStyle = 'rgba(6, 10, 14, 0.85)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    if (radar.mission?.flag) {
      const flag = radar.mission.flag;
      const o = clampToScope(scopeOffsetTo(flag, radar, k), r - MARKER_R - 1);
      diamond(ctx, c + o.x, c + o.y, MARKER_R);
      // Hollow while it sits in their fort, filled the moment somebody has hold
      // of it — the state that changes what the player should be doing is the
      // one worth a change of weight rather than a change of hue.
      if (flag.state === 'home') {
        ctx.strokeStyle = COLOURS.objective;
        ctx.lineWidth = 1.6;
        ctx.stroke();
      } else {
        ctx.fillStyle = flag.state === 'carried' ? COLOURS.ally : COLOURS.objective;
        ctx.fill();
        ctx.strokeStyle = 'rgba(6, 10, 14, 0.85)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // You, last, so nothing is drawn over the one blip that never moves.
    ctx.save();
    ctx.translate(c, c);
    ctx.rotate(radar.hullYaw);
    ctx.beginPath();
    ctx.moveTo(0, -6.5);
    ctx.lineTo(4.4, 5);
    ctx.lineTo(0, 2.6);
    ctx.lineTo(-4.4, 5);
    ctx.closePath();
    ctx.fillStyle = COLOURS.self;
    ctx.fill();
    ctx.restore();
  }

  ctx.restore();

  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.strokeStyle = COLOURS.rim;
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

// Redrawn from the prop rather than from a render loop of its own: `publishHud`
// already runs once per rendered frame, so a second loop here would either draw
// the same picture twice or draw a half-updated one.
watch(() => props.radar, draw, { deep: true, flush: 'post' });

onMounted(() => {
  draw();
  window.addEventListener('resize', draw);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', draw);
  cancelAnimationFrame(frame);
});
</script>

<template>
  <div
    class="radar"
    :class="{ compact }"
    data-test="radar"
    :data-allies="allyCount"
    :data-enemies="enemyCount"
    :data-mission="props.radar?.mission?.kind"
    aria-hidden="true"
  >
    <canvas ref="canvas" class="scope"></canvas>
    <span class="legend">
      <i class="dot ally"></i>Allies
      <i class="dot enemy"></i>Enemies
    </span>
  </div>
</template>

<style scoped>
.radar {
  position: absolute;
  right: 1.1rem;
  bottom: 1.1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  /* Never a pointer target. Aiming holds pointer lock, so anything clickable
     here would be unreachable exactly when it mattered — and letting it swallow
     a click would eat a shot. */
  pointer-events: none;
}

.scope {
  width: 168px;
  height: 168px;
  border-radius: 50%;
  /* The same dark translucent blur the lobby's panels use. Opaque would read as
     a sticker on the battlefield rather than an instrument on the glass. */
  backdrop-filter: blur(6px);
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.45);
}

.legend {
  display: flex;
  align-items: center;
  gap: 0.32rem;
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(214, 226, 232, 0.6);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
}
.dot.ally {
  background: #4fe06a;
}
.dot.enemy {
  background: #ff4a38;
  margin-left: 0.4rem;
}

@media (max-height: 720px) {
  .scope {
    width: 128px;
    height: 128px;
  }
}

/*
 * ── Compact: smaller, and out from under the thumb ──────────────────────────
 *
 * `bottom` is unset rather than overridden, because leaving it set alongside
 * `top` on a `position: absolute` box stretches it to both edges and the scope
 * ends up vertically centred with a 400px-tall parent — which draws a correct
 * radar in the wrong place and reads as a layout bug rather than a stray
 * property.
 *
 * The size lands on `.scope` and nothing else: `resize()` reads
 * `el.clientWidth`, so the canvas re-derives its own backing store at
 * `devicePixelRatio` and every tick, ring and blip is redrawn at the new scale.
 * Nothing here is a bitmap, which is why 104px is a smaller radar rather than a
 * blurry one.
 */
.radar.compact {
  top: 0.6rem;
  bottom: auto;
  right: 0.6rem;
}

.radar.compact .scope {
  width: 104px;
  height: 104px;
}

/*
 * The legend goes.
 *
 * It labels two colours a player has already met on the nameplates over the
 * tanks and in the kill feed, and at this size it is 13px of type under a 104px
 * dial — the first thing to cut when the budget is 375px of height. The colours
 * themselves are unchanged, so nothing is being explained away, only the
 * explanation.
 */
.radar.compact .legend {
  display: none;
}
</style>